// Relative import: this module is loaded by node --test.
import { hasValue } from '../../../lib/helpers/numeric.js'
import { pad } from '../../../lib/helpers/numeric.js'

export const REINFORCE_FORCE_MODELS = Object.freeze([
    { id: 'sensor1', name: 'Sensor 1', code: '6BB6', a: 1394.83, b: 87.14, match: ['6BB6'] },
    { id: 'sensor2', name: 'Sensor 2', code: '3981', a: 554.52, b: 146.98, match: ['3981'] },
    { id: 'sensor3', name: 'Sensor 3', code: 'CFC5', a: 1384.02, b: 10.693, match: ['CFC5', 'SENSOR3', 'S3'] },
    { id: 'sensor4', name: 'Sensor 4', code: 'EB84', a: 1379.02, b: 118.74, match: ['EB84', 'SENSOR4', 'S4'] },
])

const toUpper = (value) => String(value || '').toUpperCase()

const toCompactUpper = (value) => toUpper(value).replace(/[^A-Z0-9]/g, '')

export const resolveReinforceForceModelById = (id) => REINFORCE_FORCE_MODELS.find((model) => model.id === id) || null

export const resolveReinforceForceModelFromValues = (...values) => {
    const fields = values
        .filter(hasValue)
        .map((value) => String(value))
        .join(' ')

    if (!fields) return null

    const upper = toUpper(fields)
    const compact = toCompactUpper(fields)

    for (const model of REINFORCE_FORCE_MODELS) {
        for (const rawToken of model.match || []) {
            if (!rawToken) continue
            const tokenUpper = toUpper(rawToken)
            const tokenCompact = toCompactUpper(rawToken)
            if (tokenUpper && upper.includes(tokenUpper)) return model
            if (tokenCompact && compact.includes(tokenCompact)) return model
        }
    }

    return null
}

export const computeReinforceForce = (signal, model) => {
    const numericSignal = Number(signal)
    if (!Number.isFinite(numericSignal)) return null
    if (!model || !Number.isFinite(model.a) || !Number.isFinite(model.b)) return null
    const force = model.a * numericSignal + model.b
    return Number.isFinite(force) ? force : null
}

export const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const numeric = Number(timestamp)
    if (!Number.isFinite(numeric)) return 'Invalid date'
    const ms = numeric > 1e12 ? numeric : numeric * 1000
    const date = new Date(ms)
    if (isNaN(date.getTime())) return 'Invalid date'

    const year = date.getFullYear()
    const month = pad(date.getMonth() + 1)
    const day = pad(date.getDate())
    const hours = pad(date.getHours())
    const minutes = pad(date.getMinutes())

    return `${day}/${month}/${year} ${hours}:${minutes}`
}

export const formatDuration = (secs) => {
    if (!Number.isFinite(secs) || secs <= 0) return ''
    const total = Math.floor(secs)
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

export const normalizeLabel = (value) => {
    if (value == null) return ''
    const raw = String(value).trim()
    if (!raw) return ''
    return raw
        .replace(/\s+/g, '_')
        .replace(/[^A-Za-z0-9_-]/g, '')
        .toUpperCase()
        .slice(0, 12)
}

export const extractLabelFromFilename = (name) => {
    if (!name) return ''
    const base = String(name)
    if (base.startsWith('session_')) {
        const afterPrefix = base.slice('session_'.length)
        const markers = ['_v', '_c', '_utc_', '_rel_']
        let end = -1
        markers.forEach((marker) => {
            const idx = afterPrefix.indexOf(marker)
            if (idx > 0 && (end === -1 || idx < end)) end = idx
        })
        if (end > 0) {
            const candidate = afterPrefix.slice(0, end)
            const isVersionToken = candidate.startsWith('v') && /^[v][0-9_]+$/.test(candidate)
            const isCodeToken = candidate.startsWith('c') && /^[c][0-9A-Za-z]+$/.test(candidate)
            if (!isVersionToken && !isCodeToken) return normalizeLabel(candidate)
        }
    }
    const reinIdx = base.indexOf('_reinforce')
    if (reinIdx > 0) {
        let candidate = base.slice(0, reinIdx)
        candidate = candidate.replace(/[_\d]+$/, '').replace(/_+$/, '')
        if (candidate) return normalizeLabel(candidate)
    }
    return ''
}

export const formatCsv = (text, options = null) => {
    const rawText = String(text || '')
    let forceModel = null
    let forceEnabled = true
    let utcEnabled = true

    const strict = !!(options && typeof options === 'object' && options.strict === true)

    if (options && typeof options === 'object') {
        if (options.utcEnabled === false) utcEnabled = false
        if (options.forceEnabled === false) forceEnabled = false
        if (options.model === null || options.forceModel === null) forceEnabled = false

        const candidate = options.model || options.forceModel
        if (candidate && Number.isFinite(candidate.a) && Number.isFinite(candidate.b)) {
            forceModel = { a: candidate.a, b: candidate.b }
        }
        if (Number.isFinite(options.a) && Number.isFinite(options.b)) {
            forceModel = { a: options.a, b: options.b }
        }
    }

    const lines = rawText.split(/\r?\n/)
    if (lines.length === 0) return rawText

    const isMetaLine = (line) => {
        if (!line) return false
        const trimmed = line.trim()
        if (!trimmed) return false
        if (trimmed.startsWith('#')) return true
        const lower = trimmed.toLowerCase()
        return lower.startsWith('meta,') || lower.startsWith('meta=')
    }

    const metaLines = []
    let headerIndex = -1
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        if (!line || !line.trim()) continue
        if (headerIndex === -1 && isMetaLine(line)) {
            metaLines.push(line)
            continue
        }
        headerIndex = i
        break
    }
    if (headerIndex < 0) return strict ? '' : rawText

    const header = lines[headerIndex].split(',')
    const tsIdx = header.findIndex((h) => h.trim().toLowerCase() === 'timestamp')
    const vIdx = header.findIndex((h) => h.trim().toLowerCase() === 'voltage')
    const output = []
    if (metaLines.length) output.push(...metaLines)
    output.push('utc,timestamp,voltage,force_N')

    for (let i = headerIndex + 1; i < lines.length; i++) {
        const line = lines[i]
        if (!line || !line.trim() || isMetaLine(line)) continue
        const parts = line.split(',')
        if (parts.length < 2) continue

        const timestamp = parts[tsIdx >= 0 ? tsIdx : 0]
        const voltage = parseFloat(parts[vIdx >= 0 ? vIdx : 1])
        let forceValue = ''
        if (forceEnabled && Number.isFinite(voltage) && forceModel) {
            const computed = computeReinforceForce(voltage, forceModel)
            if (Number.isFinite(computed)) forceValue = computed.toFixed(3)
        }

        const ts = parseFloat(timestamp)
        const utc =
            utcEnabled && Number.isFinite(ts) ? new Date(ts > 1e12 ? ts : ts * 1000).toISOString() : ''

        output.push(
            [
                utc,
                timestamp,
                Number.isFinite(voltage) ? voltage.toFixed(4) : '',
                forceValue,
            ].join(',')
        )
    }

    return output.join('\n')
}
